import React from "react";
import { Dialog, DialogTitle } from "@headlessui/react";
import AccountingTable from "components/tables/AccountingTable";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import PropTypes from "prop-types";

const AccountingReport = ({
  openPeriods,
  selectedOpenPeriod,
  setSelectedOpenPeriod,
  operations,
  reportModalOpen,
  setReportModalOpen,
  reportTableData,
  setReportTableData,
  generateReportTableData,
  Button
}) => (
  <div>
    <div className="flex items-center gap-3 mb-2">
      <span className="text-lg font-semibold text-blue-700 tracking-wide">Reporte de Periodo</span>
    </div>
    <div className="flex flex-wrap gap-4 items-center justify-center">
      <label htmlFor="report-period-select" className="font-semibold">Por periodos activos:</label>
      <select
        id="report-period-select"
        name="report-period-select"
        value={selectedOpenPeriod}
        onChange={e => setSelectedOpenPeriod(e.target.value)}
        className="px-4 py-2 border border-blue-200 rounded shadow-sm min-w-[220px] focus:ring-2 focus:ring-blue-300 focus:border-blue-400 transition"
        style={{ minWidth: 220 }}
      >
        {openPeriods.length === 0 ? (
          <option value="">No hay periodos abiertos</option>
        ) : (
          openPeriods.map(p => (
            <option key={p.id} value={p.id}>
              {p.startDate} - {p.endDate}
            </option>
          ))
        )}
      </select>
      <button
        type="button"
        className={`ml-2 px-6 py-2 rounded font-semibold transition shadow ${(() => {
          const period = openPeriods.find(p => p.id.toString() === selectedOpenPeriod.toString());
          if (!period) return 'bg-gray-300 text-gray-500 cursor-not-allowed';
          const hasOps = operations.some(o => o.date >= period.startDate && o.date <= period.endDate);
          return hasOps ? 'bg-primary-500 text-white hover:bg-primary-700' : 'bg-gray-300 text-gray-500 cursor-not-allowed';
        })()}`}
        disabled={(() => {
          const period = openPeriods.find(p => p.id.toString() === selectedOpenPeriod.toString());
          if (!period) return true;
          return !operations.some(o => o.date >= period.startDate && o.date <= period.endDate);
        })()}
        onClick={() => {
          setReportTableData(generateReportTableData());
          setReportModalOpen(true);
        }}
      >
        Reporte del periodo
      </button>
      {/* Modal for period report preview */}
      {reportModalOpen && (
        <Dialog open={reportModalOpen} onClose={() => setReportModalOpen(false)} as="div" className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black bg-opacity-25" aria-hidden="true" />
          <div className="bg-white rounded-lg p-8 shadow-lg w-full max-w-3xl z-50 relative">
            <DialogTitle className="text-xl font-bold mb-4">Reporte del Periodo</DialogTitle>
            <AccountingTable title="Balance General" tableData={reportTableData} />
            <div className="flex justify-end mt-4 gap-2">
              <Button
                onClick={() => {
                  // Export to Excel
                  const wsData = [
                    ["Concepto", "Ingreso", "Egreso", "Total"],
                    ...reportTableData.map(row => [
                      row.category,
                      row.income,
                      row.outcome,
                      row.balance
                    ])
                  ];
                  const ws = XLSX.utils.aoa_to_sheet(wsData);
                  const wb = XLSX.utils.book_new();
                  XLSX.utils.book_append_sheet(wb, ws, "Reporte");
                  const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
                  saveAs(new Blob([wbout], { type: "application/octet-stream" }), "reporte_periodo.xlsx");
                }}
                className="bg-green-500 hover:bg-green-700"
              >
                Exportar a Excel
              </Button>
              <Button
                onClick={() => {
                  // Export to PDF
                  const doc = new jsPDF();
                  doc.setFontSize(16);
                  doc.text("Reporte del Periodo", 14, 18);
                  doc.setFontSize(12);
                  doc.text("Balance General", 14, 28);
                  const tableColumn = ["Concepto", "Ingreso", "Egreso", "Total"];
                  const tableRows = reportTableData.map(row => [row.category, row.income, row.outcome, row.balance]);
                  autoTable(doc, {
                    head: [tableColumn],
                    body: tableRows,
                    startY: 32,
                    styles: { fontSize: 10 },
                    headStyles: { fillColor: [41, 128, 185] },
                  });
                  doc.save("reporte_periodo.pdf");
                }}
                className="bg-red-500 hover:bg-red-700"
              >
                Exportar a PDF
              </Button>
              <Button onClick={() => setReportModalOpen(false)} className="bg-gray-400 hover:bg-gray-600">Cerrar</Button>
            </div>
          </div>
        </Dialog>
      )}
    </div>
  </div>
);

AccountingReport.propTypes = {
    openPeriods: PropTypes.array.isRequired,
    operations: PropTypes.array.isRequired,
    selectedOpenPeriod: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    setSelectedOpenPeriod: PropTypes.func.isRequired,
    reportModalOpen: PropTypes.bool.isRequired,
    setReportModalOpen: PropTypes.func.isRequired,
    reportTableData: PropTypes.array.isRequired,
    setReportTableData: PropTypes.func.isRequired,
    generateReportTableData: PropTypes.func.isRequired,
    Button: PropTypes.elementType.isRequired
}

export default AccountingReport;
